import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslation } from "react-i18next";

// Icons
import InsightsIcon from "@mui/icons-material/Insights";
import GroupsIcon from "@mui/icons-material/Groups";
import PublicIcon from "@mui/icons-material/Public";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

// Styled Stat Card with 3D effect
const StatCard = styled(Paper)(({ theme }) => ({
  minWidth: 250,
  padding: theme.spacing(4),
  borderRadius: "20px",
  background: "linear-gradient(145deg, #ffffff, #e6e6e6)", // Default background
  boxShadow: `
    0 10px 20px rgba(0, 0, 0, 0.15),
    0 6px 6px rgba(0, 0, 0, 0.1),
    inset 0 -1px 1px rgba(0, 0, 0, 0.05)
  `,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  height: 320,
  transition: "transform 0.5s ease, box-shadow 0.5s ease",
  transformStyle: "preserve-3d",
  perspective: "1000px",
  position: "relative",
  overflow: "hidden",
  border: "1px solid rgba(0,0,0,0.1)",
  "& .MuiIcon-root": {
    transition: "color 0.3s ease, transform 0.3s ease", // Smooth transition for icon color
  },

  // Hover effect on the entire card
  "&:hover": {
    transform: "rotateY(8deg) rotateX(6deg) scale(1.04)", // Apply transform on hover
    boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.3),
      0 8px 12px rgba(0, 0, 0, 0.1),
      inset 0 2px 4px rgba(255, 255, 255, 0.4)
    `,
    background: "linear-gradient(145deg, #0C2182, #1a2aa5)", // Card turns blue on hover
    color: "#fff", // Text color changes to white
    "& .MuiIcon-root": {
      color: "white", // Icon turns white on card hover
      transform: "scale(1.2) rotateY(10deg)", // Apply scaling to the icon on hover
    },
  },

  "@keyframes shine": {
    "0%": { transform: "rotate(25deg) translateX(-100%)" },
    "100%": { transform: "rotate(25deg) translateX(100%)" },
  },
}));

// Individual Stat Card Item
const StatCardItem = ({ number, suffix, label, description, icon: Icon }) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const { i18n } = useTranslation();
  const direction = i18n.dir();
  const fontFamily =
    direction === "rtl" ? "'Tajawal', Arial, sans-serif" : "Arial, Helvetica, sans-serif";

  return (
    <StatCard ref={ref} sx={{ fontFamily }} data-aos="fade-up">
      <Box sx={{ zIndex: 2 }}>
        {Icon && (
          <Icon
            sx={{
              fontSize: 50,
             
              mb: 2,
              filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.4))",
              transform: inView ? "translateZ(20px)" : "none",
            }}
          />
        )}
        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          sx={{
            fontSize: {
              xs: "1.8rem",
              sm: "2rem",
              md: "2.2rem",
              lg: "2.4rem",
            },
            fontFamily,
            textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
          }}
        >
          {inView ? (
            <CountUp start={0} end={number} duration={2} separator="," suffix={suffix} />
          ) : (
            "0"
          )}
        </Typography>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: "gray",
            fontWeight: 500,
            fontSize: {
              xs: "1rem",
              sm: "1.1rem",
              md: "1.2rem",
            },
            fontFamily,
          }}
        >
          {label}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{
          mt: 2,
          fontSize: {
            xs: "0.85rem",
            sm: "0.95rem",
            md: "1rem",
          },
          fontFamily,
          textAlign: "justify",
          zIndex: 2,
        }}
      >
        {description}
      </Typography>
    </StatCard>
  );
};

// Main StatCards Component
const StatCards = () => {
  const { t, i18n } = useTranslation();
  const [showCards, setShowCards] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const direction = i18n.dir();
  const fontFamily =
    direction === "rtl" ? "'Tajawal', Arial, sans-serif" : "Arial, Helvetica, sans-serif";

  useEffect(() => {
    AOS.init({ duration: 1200 });
  }, []);

  const stats = [
    {
      number: 4000,
      label: t("Cardh1"),
      description: t("stat1"),
      icon: GroupsIcon,
    },
    {
      number: 50,
      suffix: "+",
      label: t("Cardh2"),
      description: t("stat2"),
      icon: PublicIcon,
    },
    {
      number: 330,
        suffix: "+",
      label: t("Cardh3"),
      description: t("stat3"),
      icon: ThumbUpIcon,
    },
    {
      number: 22,
      suffix: "",
      label: t("Cardh4"),
      description: t("stat4"),
      icon: EventAvailableIcon,
    },
  ];

  return (
    <Box sx={{ padding: "15px", fontFamily }}>
      {isSmallScreen && (
        <Box display="flex" justifyContent="center" mb={2}>
          <IconButton onClick={() => setShowCards(!showCards)} style={{ background: "blue" }}>
            <InsightsIcon fontSize="large" style={{ color: "white" }} />
          </IconButton>
        </Box>
      )}

      <Collapse in={!isSmallScreen || showCards}>
        <Grid
          container
          spacing={4}
          justifyContent="center"
          alignItems="stretch"
          sx={{
            marginTop: {
              xs: 0,
              sm: 0,
              md: -25,
            },
          }}
        >
          {stats.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={index}>
              <StatCardItem {...item} />
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Box>
  );
};

export default StatCards;
